// CORRECT A MISTYPED EXCHANGE RATE -- a small dialog, ADMIN ONLY.
//
// Owner decisions: 2026-09-23 (fx-rate-correction) for the RULES, 2026-09-30
// (settings-fx-layout) for the UX: no permanent correction panel. The rate
// history table shows "Correct" only on rows the SERVER says are correctable
// (FxRate.correctable: starts today or later AND no finalised quote used it),
// and that action opens this dialog.
//
// ⚠️ THE DIALOG IS NOT THE GUARD. /correct_fx_rate refuses Sales and Viewer
// with a 403 and re-checks every rule on save; its reason is shown verbatim
// (it names the finalised quote when that is the cause). The rate VALUE only
// changes -- currency, dates and source never move.
//
// ⚠️ THE >10% TYPO GUARD APPLIES, against the value being corrected: it asks,
// and saves on confirm.

import { useState } from "react";
import { correctFxRate } from "@/services/api";
import { percentDifferent, TYPO_GUARD_PCT } from "@/services/fxInForce";
import { TYPO_GUARD_TEXT } from "@/components/PublishFxRate";
import type { FxRate } from "@/types";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, boxSizing: "border-box",
};
const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const LABEL: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const VALUE: React.CSSProperties = { fontSize: typography.fontSize.base, color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums" };

export function CorrectFxRateDialog({ rate, onClose, onCorrected }: {
  rate: FxRate;
  onClose: () => void;
  /** Called with the confirmation text after the server really corrected it. */
  onCorrected: (message: string) => void;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmPct, setConfirmPct] = useState<number | null>(null);

  const submit = async (confirmed: boolean) => {
    setError("");
    const next = Number(value);
    if (value.trim() === "" || !(next > 0) || !Number.isFinite(next)) {
      setError("A rate must be a number greater than 0.");
      return;
    }
    const pct = percentDifferent(next, rate.rate);
    if (!confirmed && pct !== null && pct > TYPO_GUARD_PCT) {
      setConfirmPct(pct);
      return;
    }
    setBusy(true);
    const result = await correctFxRate(rate.id, next);
    setBusy(false);
    setConfirmPct(null);
    if (!result.ok) {
      setError(result.error ?? "The rate was not corrected.");
      return;
    }
    onCorrected(`Corrected ${rate.currency} from ${rate.effectiveFrom}: ${rate.rate} → ${next}.`);
  };

  return (
    <div
      data-testid="fx-correct-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 50, backgroundColor: semanticColors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16],
      }}
    >
      <div
        data-testid="fx-correct-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Correct exchange rate"
        style={{
          width: layout.modalWidth, maxWidth: "100%", backgroundColor: colors.white,
          borderRadius: radius.base, boxShadow: shadows.modal, padding: spacing[20],
          display: "flex", flexDirection: "column", gap: spacing[12],
        }}
      >
        <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary }}>
          Correct a mistyped rate
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: `${spacing[6]}px ${spacing[16]}px` }}>
          <span style={LABEL}>Currency</span>
          <span data-testid="fx-correct-currency" style={VALUE}>{rate.currency}</span>
          <span style={LABEL}>Effective from</span>
          <span data-testid="fx-correct-from" style={VALUE}>{rate.effectiveFrom ? formatDate(rate.effectiveFrom) : "—"}</span>
          <span style={LABEL}>Current value</span>
          <span data-testid="fx-correct-current" style={VALUE}>{rate.rate}</span>
          <label htmlFor="fx-correct-rate" style={{ ...LABEL, alignSelf: "center" }}>New value</label>
          <input
            id="fx-correct-rate"
            data-testid="fx-correct-rate"
            type="number" step="any" min="0" autoFocus
            value={value}
            onChange={(e) => { setValue(e.target.value); setConfirmPct(null); setError(""); }}
            style={{ ...INPUT, width: 160 }}
          />
        </div>
        <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
          Only the value changes. The server re-checks that this rate starts today or later and
          that no finalised quote was priced at it.
        </div>

        {confirmPct !== null && (
          <div
            data-testid="fx-correct-typo-guard"
            style={{
              padding: spacing[8], borderRadius: radius.base,
              backgroundColor: colors.amber50, border: `1px solid ${colors.amber200}`,
              color: colors.amber800, fontSize: typography.fontSize.sm,
              display: "flex", alignItems: "center", gap: spacing[8], flexWrap: "wrap",
            }}
          >
            <span>{TYPO_GUARD_TEXT(confirmPct)}</span>
            <button data-testid="fx-correct-confirm" onClick={() => void submit(true)} disabled={busy} style={BUTTON}>
              Correct anyway
            </button>
          </div>
        )}

        {error !== "" && (
          <div
            data-testid="fx-correct-error"
            role="alert"
            style={{
              padding: spacing[8], borderRadius: radius.base,
              backgroundColor: semanticColors.destructiveBg,
              border: `1px solid ${semanticColors.destructiveBorder}`,
              color: semanticColors.destructive, fontSize: typography.fontSize.sm,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[8] }}>
          <button data-testid="fx-correct-cancel" onClick={onClose} disabled={busy} style={BUTTON}>Cancel</button>
          <button data-testid="fx-correct-save" onClick={() => void submit(false)} disabled={busy}
                  style={{ ...PRIMARY, cursor: busy ? "not-allowed" : "pointer" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
